import { i18n } from "../i18n";
import type { PremisesCensus, Recent } from "../premises_census";
import type { NightClock } from "./night_clock";
import type { Person } from "./people_list";

interface Targets {
  list: HTMLElement;
  template: HTMLTemplateElement;
  empty: HTMLElement;
}

// The last few door events at the pointer, built from the events the census
// already holds, so the excerpt moves with the scrubber instead of after it.
export class Excerpt {
  private paintedPointer = -1;

  constructor(
    private readonly targets: Targets,
    private readonly clock: NightClock,
    private readonly personOf: (id: number) => Person | undefined,
    private readonly size: number,
  ) {}

  // `arrived` names the events that landed live; their rows fade in, and the
  // ids are consumed as they are drawn.
  paint(census: PremisesCensus, arrived: Set<number>) {
    if (census.position === this.paintedPointer) return;
    this.paintedPointer = census.position;
    const list = this.targets.list;
    const kept = new Map(Array.from(list.children, (row) => [(row as HTMLElement).dataset.eventId, row as HTMLElement]));
    const rows = census.recent(this.size).map((event) => kept.get(String(event.id)) ?? this.rowFor(event, arrived.delete(event.id)));
    // Rows already on the page stay where they are, so one still fading in is not restarted by the next to land.
    Array.from(list.children).forEach((row) => { if (!rows.includes(row as HTMLElement)) row.remove(); });
    rows.forEach((row, index) => { if (list.children[index] !== row) list.insertBefore(row, list.children[index] ?? null); });
    this.targets.empty.hidden = rows.length > 0;
  }

  private rowFor(event: Recent, arrived: boolean): HTMLElement {
    const row = this.targets.template.content.firstElementChild!.cloneNode(true) as HTMLElement;
    const person = this.personOf(event.personId);
    const entering = event.dir === 1;
    row.dataset.eventId = String(event.id);
    if (arrived) row.classList.add("door-log-arrived");
    row.querySelector<HTMLElement>("[data-role=in]")!.hidden = !entering;
    row.querySelector<HTMLElement>("[data-role=out]")!.hidden = entering;
    row.querySelector<HTMLElement>("[data-role=chip]")!.dataset.direction = entering ? "in" : "out";
    row.querySelector<HTMLElement>("[data-role=name]")!.textContent = person?.name ?? "";
    const method = event.overridden ? "override" : event.method;
    row.querySelector<HTMLElement>("[data-role=meta]")!.textContent = [
      i18n.t(`premises.excerpt.${entering ? "in" : "out"}`),
      i18n.t(`premises.excerpt.${method}`),
      person?.bed || i18n.t("premises.excerpt.no_bed"),
    ].join(" · ");
    row.querySelector<HTMLElement>("[data-role=time]")!.textContent = this.clock.timeAt(event.t);
    return row;
  }
}
